import { calculateConfidenceScore, levenshteinDistance, removeAccents } from './nameMatching';

export interface AddressMatchCandidate {
  immatriculation: string;
  name: string;
  address: string;
}

export interface AddressMatchResult {
  fileEntry: AddressMatchCandidate;
  rnicEntry: AddressMatchCandidate;
  similarity: number;
  confidenceScore: number;
}

const normalizeAddress = (address: string): string => {
  if (!address) {return '';}
  let normalized = removeAccents(address.toLowerCase());
  normalized = normalized.replaceAll(/[.,;/\\#\-]/g, ' ');
  normalized = normalized.replaceAll(/\s+/g, ' ').trim();
  return normalized;
};

const splitAroundPostalCode = (address: string): { normalized: string; prefix: string; postal: string | null; suffix: string } => {
  const normalized = normalizeAddress(address);
  if (!normalized) {return { normalized: '', prefix: '', postal: null, suffix: '' };}

  const postalMatch = normalized.match(/\b\d{4,5}\b/);
  if (!postalMatch || postalMatch.index === undefined) {
    return { normalized, prefix: normalized, postal: null, suffix: '' };
  }

  const idx = postalMatch.index;
  const postal = postalMatch[0];
  const prefix = normalized.slice(0, idx).trim();
  const suffix = normalized.slice(idx + postal.length).trim();

  return { normalized, prefix, postal, suffix };
};

const similarity = (a: string, b: string): number => {
  if (!a || !b) {return 0;}
  if (a === b) {return 1;}
  const distance = levenshteinDistance(a, b);
  const maxLength = Math.max(a.length, b.length);
  if (maxLength === 0) {return 1;}
  return 1 - (distance / maxLength);
};

export const calculateAddressSimilarity = (addr1: string, addr2: string): number => {
  const parts1 = splitAroundPostalCode(addr1);
  const parts2 = splitAroundPostalCode(addr2);

  if (!parts1.normalized || !parts2.normalized) {return 0;}
  if (parts1.normalized === parts2.normalized) {return 1;}

  // If both have a postal code, weight postal highly, then prefix/suffix.
  if (parts1.postal && parts2.postal) {
    const postalScore = parts1.postal === parts2.postal ? 1 : 0;
    const prefixScore = similarity(parts1.prefix, parts2.prefix);
    const suffixScore = similarity(parts1.suffix, parts2.suffix);

    // Weights favour exact postal code, then the main street part, then trailing city info.
    const weighted =
      (0.4 * postalScore) +
      (0.35 * prefixScore) +
      (0.25 * suffixScore);

    return Math.min(1, Math.max(0, weighted));
  }

  // Fallback to overall fuzzy similarity when postal code is missing on one/both sides.
  return similarity(parts1.normalized, parts2.normalized);
};
export const findAddressMatches = (
  fileEntries: AddressMatchCandidate[],
  rnicEntries: AddressMatchCandidate[],
  threshold = 0.55,
): AddressMatchResult[] => {
  const matches: AddressMatchResult[] = [];
  const usedRnic = new Set<number>();

  const sortableFileEntries = [...fileEntries].sort((a, b) => b.address.length - a.address.length);

  for (const fileEntry of sortableFileEntries) {
    let bestMatch: AddressMatchResult | null = null;
    let bestSimilarity = threshold;
    let bestRnicIndex = -1;

    for (let i = 0; i < rnicEntries.length; i++) {
      if (usedRnic.has(i)) {continue;}
      const rnicEntry = rnicEntries[i];
      const addressSimilarity = calculateAddressSimilarity(fileEntry.address, rnicEntry.address);

      if (addressSimilarity > bestSimilarity) {
        bestSimilarity = addressSimilarity;
        bestRnicIndex = i;
        bestMatch = {
          fileEntry,
          rnicEntry,
          similarity: addressSimilarity,
          confidenceScore: calculateConfidenceScore(addressSimilarity),
        };
      }
    }

    if (bestMatch && bestRnicIndex >= 0) {
      matches.push(bestMatch);
      usedRnic.add(bestRnicIndex);
    }
  }

  return matches.sort((a, b) => b.confidenceScore - a.confidenceScore);
};
